import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extensions";
import { Form, Input, Modal, Space, Tooltip, Button } from "antd";
import { BoldOutlined, ItalicOutlined, LinkOutlined } from "@ant-design/icons";
import { FaUnderline } from "react-icons/fa6";
import "./RichTextEditor.css";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";

export function getPlainTextLength(html) {
  if (!html) return 0;
  if (typeof document === "undefined")
    return String(html).replace(/<[^>]*>/g, "").length;
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").length;
}

function RichTextEditorField({
  value,
  onChange,
  placeholder,
  maxLength,
  disabled,
}) {
  const { status: fieldStatus } = Form.Item.useStatus();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [focused, setFocused] = useState(false);
  const lastEmittedHtml = useRef(null);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        strike: false,
        link: {
          openOnClick: false,
          defaultProtocol: "https",
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Write something…",
      }),
    ],
    [placeholder],
  );

  const editor = useEditor(
    {
      extensions,
      content: value || "",
      editable: !disabled,
      editorProps: {
        attributes: {
          class: "tiptap",
          "aria-label": placeholder || "Rich text",
        },
        handleKeyDown: (_view, event) => {
          // Keep the content single-line for downstream UI/payload safety.
          if (event.key === "Enter") {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();
        const normalizedHtml = html.replace(/\r?\n/g, " ");
        lastEmittedHtml.current = normalizedHtml;
        onChange?.(normalizedHtml);
      },
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
    },
    [extensions],
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const incoming = value ?? "";
    if (incoming === lastEmittedHtml.current) return;
    const current = editor.getHTML();
    if (incoming === current) return;
    editor.commands.setContent(incoming || "", false);
    lastEmittedHtml.current = editor.getHTML();
  }, [value, editor]);

  const openLinkModal = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    setLinkUrl(previous);
    setLinkOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = (linkUrl || "").trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: normalized })
        .run();
    }
    setLinkOpen(false);
    setLinkUrl("");
  }, [editor, linkUrl]);

  const closeLinkModal = useCallback(() => {
    setLinkOpen(false);
    setLinkUrl("");
  }, []);

  const plainLen = editor
    ? getPlainTextLength(editor.getHTML())
    : getPlainTextLength(value);
  const overLimit = maxLength != null && plainLen > maxLength;

  const wrapClass = [
    "rich-text-editor",
    focused ? "rich-text-editor-focused" : "",
    fieldStatus === "error" ? "rich-text-editor-error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!editor) {
    return <div className={wrapClass} style={{ minHeight: 168 }} />;
  }

  return (
    <>
      <div className={wrapClass}>
        <div className="rich-text-editor-toolbar">
          <Space size={4} wrap>
            <Tooltip title="Bold">
              <Button
                type={editor.isActive("bold") ? "primary" : "default"}
                size="small"
                icon={<BoldOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={disabled}
              />
            </Tooltip>
            <Tooltip title="Italic">
              <Button
                type={editor.isActive("italic") ? "primary" : "default"}
                size="small"
                icon={<ItalicOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={disabled}
              />
            </Tooltip>
            <Tooltip title="Underline">
              <Button
                type={editor.isActive("underline") ? "primary" : "default"}
                size="small"
                icon={<FaUnderline />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={disabled}
              />
            </Tooltip>
            <Tooltip title="Link — select text first, then set URL">
              <Button
                type={editor.isActive("link") ? "primary" : "default"}
                size="small"
                icon={<LinkOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={openLinkModal}
                disabled={disabled}
              />
            </Tooltip>
          </Space>
        </div>
        <EditorContent editor={editor} />
      </div>
      {maxLength != null && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: overLimit ? "#ff4d4f" : "rgba(0,0,0,0.45)",
            textAlign: "right",
          }}
        >
          {plainLen} / {maxLength}
        </div>
      )}
      <Modal
        title={editor.isActive("link") ? "Edit link" : "Insert link"}
        open={linkOpen}
        closable={false}
        closeIcon={false}
        onCancel={closeLinkModal}
        footer={
          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center justify-center"
              onClick={applyLink}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              Apply
            </Button>
            <Button className="reset-button" onClick={closeLinkModal}>
              Cancel
            </Button>
          </div>
        }
        destroyOnClose
      >
        <p style={{ marginBottom: 8, color: "rgba(0,0,0,0.65)", fontSize: 13 }}>
          Select text in the editor, then enter a URL. Leave empty and apply to
          remove the link.
        </p>
        <Input
          placeholder="https://example.com"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={applyLink}
          allowClear
        />
      </Modal>
    </>
  );
}

const RichTextEditor = ({
  label,
  name,
  placeholder,
  maxLength,
  required,
  rules = [],
  disable,
  errorMessage,
  ...rest
}) => {
  const maxRule = useMemo(() => {
    if (maxLength == null) return [];
    return [
      {
        validator: (_, value) => {
          const len = getPlainTextLength(value);
          if (len > maxLength) {
            return Promise.reject(
              new Error(
                `${label} must be at most ${maxLength} characters (text only)`,
              ),
            );
          }
          return Promise.resolve();
        },
      },
    ];
  }, [label, maxLength]);

  const defaultRules = [
    {
      required,
      message: `${label} is required`,
    },
    ...maxRule,
  ];

  const combinedError = errorMessage;

  return (
    <Form.Item
      label={
        required ? (
          <span>
            {label}
            <span style={{ color: "red" }}>*</span>
          </span>
        ) : (
          label
        )
      }
      className="mb-2"
      name={name}
      rules={rules.length ? rules : defaultRules}
      validateStatus={combinedError ? "error" : ""}
      help={combinedError}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      required={false}
      {...rest}
      getValueFromEvent={(v) => v}
    >
      <RichTextEditorField
        placeholder={placeholder ?? `Enter ${label}`}
        maxLength={maxLength}
        disabled={disable}
      />
    </Form.Item>
  );
};

export default RichTextEditor;
